import { ClipboardEvent, FC, useRef, useState } from 'react'
import { cn } from '@wellesley/ui'
import { logger } from 'helpers/logger'

export type InputsProps = {
  className?: string
  length?: number
  isError?: boolean
  onComplete: (code: number) => void
  onChange?: (code: number) => void
  onFullFilled?: (isFullFilled: boolean) => void
}

const isNumber = (value: string) => /^\d*$/.test(value)

export const Inputs: FC<InputsProps> = ({
  length = 6,
  onComplete,
  onChange,
  isError,
  className,
  onFullFilled,
}) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const checkIfFullFilled = (code: string) => {
    if (code.length === length) onFullFilled?.(true)
    else onFullFilled?.(false)
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    try {
      const newValues = event.clipboardData.getData('text').slice(0, length)
      if (!isNumber(newValues)) return

      const newValuesArray = newValues.split('')
      setValues((prev) => prev.map((value, i) => newValuesArray[i] || value))

      if (newValues.length === length) onComplete(+newValues)
      checkIfFullFilled(newValues)
      onChange?.(+newValues)
    } catch (error) {
      logger.error('Error reading from clipboard: ', error)
    }
  }

  const handleChange = (index: number, value: string) => {
    if (!isNumber(value)) return

    const newValues = [...values]
    newValues[index] = value
    setValues(newValues)

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    if (newValues.every((val) => val !== '')) {
      onComplete(+newValues.join(''))
    }

    checkIfFullFilled(newValues.join(''))
    onChange?.(+newValues.join(''))
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className={cn('gap-xxs flex flex-row justify-center', className)}>
      {values.map((value, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value}
          onPaste={handlePaste}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          ref={(el) => (inputRefs.current[index] = el)}
          className={cn(
            `rounded-m px-s py-l w-[60px] border text-center text-2xl font-normal text-gray-900 ring-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`,
            isError && 'border-2 border-red-500 bg-red-50 focus-visible:ring-0',
          )}
        />
      ))}
    </div>
  )
}
